import { Button } from '@suscri/ui';
import { BellRing, BellOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  getCurrentPushSubscription,
  isPushSupported,
  subscribeToPush,
  unsubscribeFromPush,
} from '../lib/push';
import { useToast } from '../lib/toast';
import { trpc } from '../lib/trpc';

// Activación de notificaciones push del navegador (M7, PWA).
export function PushSettings() {
  const { t } = useTranslation();
  const toast = useToast();
  const supported = isPushSupported();
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);

  const { data: keyData } = trpc.push.publicKey.useQuery(undefined, {
    enabled: supported,
  });
  const subscribeMutation = trpc.push.subscribe.useMutation();
  const unsubscribeMutation = trpc.push.unsubscribe.useMutation();

  useEffect(() => {
    if (!supported) return;
    getCurrentPushSubscription().then(sub => setEnabled(Boolean(sub)));
  }, [supported]);

  if (!supported) {
    return (
      <p className="text-sm text-gray-500">
        {t('Este navegador no soporta notificaciones push.')}
      </p>
    );
  }

  const enable = async () => {
    if (!keyData?.publicKey) return;
    setBusy(true);
    try {
      const sub = await subscribeToPush(keyData.publicKey);
      if (!sub) {
        toast.error(
          t(
            'No se pudo activar. Comprueba que no has bloqueado las notificaciones para este sitio.'
          )
        );
        return;
      }
      await subscribeMutation.mutateAsync(sub);
      setEnabled(true);
      toast.success(t('Notificaciones push activadas en este dispositivo.'));
    } catch {
      toast.error(t('No se pudieron activar las notificaciones push.'));
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const endpoint = await unsubscribeFromPush();
      if (endpoint) {
        await unsubscribeMutation.mutateAsync({ endpoint });
      }
      setEnabled(false);
      toast.success(t('Notificaciones push desactivadas en este dispositivo.'));
    } catch {
      toast.error(t('No se pudieron desactivar.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <p className="text-sm font-medium text-gray-900">
          {t('Notificaciones push del navegador')}
        </p>
        <p className="text-xs text-gray-500">
          {t(
            'Recibe las alertas de renovación y presupuesto aunque la app esté cerrada. Se activan por dispositivo.'
          )}
        </p>
      </div>
      {enabled ? (
        <Button variant="outline" onClick={disable} disabled={busy}>
          <BellOff className="mr-2 h-4 w-4" />
          {busy ? t('Desactivando…') : t('Desactivar en este dispositivo')}
        </Button>
      ) : (
        <Button onClick={enable} disabled={busy || enabled === null}>
          <BellRing className="mr-2 h-4 w-4" />
          {busy ? t('Activando…') : t('Activar notificaciones')}
        </Button>
      )}
    </div>
  );
}
